import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Grid, IconButton, Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, InputAdornment, Divider, Badge, LinearProgress,
  TablePagination,
} from '@mui/material';
import {
  Add, ArrowBack, Refresh, Delete, YouTube, PictureAsPdf,
  Notes, Link as LinkIcon, Visibility, Search, LibraryBooks,
  CloudUpload, OpenInNew, School, Edit,
} from '@mui/icons-material';
import { InvokeApi, apiList, postImg } from '../../services/ApiService';
import { Config } from '../../config/Config';
import ClassSectionGrid from '../../components/ClassSectionGrid';
import dayjs from 'dayjs';

// ── helpers ──────────────────────────────────────────────────────────────────
const TYPE_META = {
  PDF: { color: '#e53935', bg: '#ffebee', icon: <PictureAsPdf sx={{ fontSize: 16 }} /> },
  Video: { color: '#FF0000', bg: '#fff3e0', icon: <YouTube sx={{ fontSize: 16 }} /> },
  Note: { color: '#1565c0', bg: '#e3f2fd', icon: <Notes sx={{ fontSize: 16 }} /> },
  Link: { color: '#2e7d32', bg: '#e8f5e9', icon: <LinkIcon sx={{ fontSize: 16 }} /> },
};

const TypeChip = ({ type }) => {
  const m = TYPE_META[type] || TYPE_META.Note;
  return (
    <Chip
      icon={React.cloneElement(m.icon, { style: { color: m.color } })}
      label={type}
      size="small"
      sx={{ background: m.bg, color: m.color, fontWeight: 700, fontSize: 11, border: `1px solid ${m.color}22` }}
    />
  );
};

const EMPTY_FORM = {
  title: '', description: '', subject: '',
  material_type: 'Note', youtube_url: '',
};

// ── main component ────────────────────────────────────────────────────────────
export default function StudyMaterials() {
  const [view, setView] = useState('grid');   // 'grid' | 'list'
  const [selected, setSelected] = useState(null);     // { standard, section, acYear }
  const [subjects, setSubjects] = useState([]);
  const [activeSubject, setActiveSubject] = useState('All');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // dialog states
  const [addDialog, setAddDialog] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // progress edit state
  const [progressDialog, setProgressDialog] = useState(false);
  const [progressItem, setProgressItem] = useState(null);
  const [progressValue, setProgressValue] = useState(0);
  const [savingProgress, setSavingProgress] = useState(false);

  // ── load subjects for a class ──────────────────────────────────────────────
  const loadSubjects = useCallback(async (cls) => {
    const data = await InvokeApi(Config.main_url + apiList.getSubjects, {
      standard: cls.standard, section: cls.section,
    });
    setSubjects(data?.subjects || []);
  }, []);

  // ── load materials ─────────────────────────────────────────────────────────
  const loadMaterials = useCallback(async (cls, subject = '') => {
    if (!cls) return;
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getStudyMaterials, {
      standard: cls.standard,
      section: cls.section,
      academic_year: cls.acYear,
      subject: subject === 'All' ? '' : subject,
    });
    setMaterials(data?.materials || []);
    setLoading(false);
  }, []);

  // ── class card click ───────────────────────────────────────────────────────
  const handleSelect = (cls) => {
    setSelected(cls);
    setView('list');
    setActiveSubject('All');
    setMaterials([]);
    setSearch('');
    loadSubjects(cls);
    loadMaterials(cls, '');
  };

  // ── subject tab click ──────────────────────────────────────────────────────
  const handleSubjectTab = (subj) => {
    setActiveSubject(subj);
    loadMaterials(selected, subj);
  };

  // ── save new material ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.title.trim() || !form.subject || !form.material_type) {
      alert('Title, Subject and Type are required'); return;
    }
    if (form.material_type === 'Video' && !form.youtube_url.trim()) {
      alert('YouTube URL is required for Video type'); return;
    }
    setSaving(true);

    // 1. create the record
    const res = await InvokeApi(Config.main_url + apiList.addStudyMaterial, {
      standard: selected.standard,
      section: selected.section,
      academic_year: selected.acYear,
      subject: form.subject,
      title: form.title,
      description: form.description,
      material_type: form.material_type,
      youtube_url: form.youtube_url,
    });

    if (res?.code !== '200') {
      alert(res?.message || 'Failed to save'); setSaving(false); return;
    }

    // 2. upload PDF if provided
    if (pdfFile && res.material_id) {
      const fd = new FormData();
      fd.append('file', pdfFile);
      fd.append('id', res.material_id);
      await postImg(Config.main_url + apiList.uploadStudyMaterialFile, fd);
    }

    setSaving(false);
    setAddDialog(false);
    setForm(EMPTY_FORM);
    setPdfFile(null);
    loadMaterials(selected, activeSubject);
  };

  // ── delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this material?')) return;
    await InvokeApi(Config.main_url + apiList.deleteStudyMaterial, { id });
    loadMaterials(selected, activeSubject);
  };

  // ── open progress edit dialog ──────────────────────────────────────────────
  const openProgressDialog = (m) => {
    setProgressItem(m);
    setProgressValue(parseInt(m.progress ?? 0, 10));
    setProgressDialog(true);
  };

  // ── save progress ──────────────────────────────────────────────────────────
  const handleSaveProgress = async () => {
    if (!progressItem) return;
    setSavingProgress(true);
    const res = await InvokeApi(Config.main_url + apiList.updateStudyMaterialProgress, {
      id: progressItem.ID,
      progress: progressValue,
    });
    setSavingProgress(false);
    if (res?.code === '200') {
      setProgressDialog(false);
      setProgressItem(null);
      // update local state so table refreshes instantly
      setMaterials(prev => prev.map(m =>
        m.ID === progressItem.ID ? { ...m, progress: progressValue } : m
      ));
    } else {
      alert(res?.message || 'Failed to update progress');
    }
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // ── filtered list ──────────────────────────────────────────────────────────
  const filtered = materials.filter(m =>
    !search || m.title.toLowerCase().includes(search.toLowerCase()) ||
    m.subject.toLowerCase().includes(search.toLowerCase())
  );

  // ── counts per subject ─────────────────────────────────────────────────────
  const countFor = (subj) =>
    subj === 'All' ? materials.length : materials.filter(m => m.subject === subj).length;
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (view === 'grid') {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LibraryBooks sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#1a237e">Study Materials</Typography>
              <Typography variant="body2" color="text.secondary">Notes · PDFs · Video Lessons — per class &amp; subject</Typography>
            </Box>
          </Box>
        </Box>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', p: 3 }}>
          <ClassSectionGrid onSelect={handleSelect} />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg,#1a237e,#1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LibraryBooks sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#1a237e">Study Materials</Typography>
            <Typography variant="body2" color="text.secondary">
              {selected?.standard} – {selected?.section} · {selected?.acYear} · {materials.length} items
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => setView('grid')}
            sx={{ borderRadius: 2, color: '#1a237e', borderColor: '#1a237e' }}>Back</Button>
          <Tooltip title="Refresh">
            <IconButton onClick={() => loadMaterials(selected, activeSubject)}
              sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />}
            onClick={() => { setForm({ ...EMPTY_FORM, subject: activeSubject === 'All' ? '' : activeSubject }); setAddDialog(true); }}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Material
          </Button>
        </Box>
      </Box>

      {/* ── Subject Tabs ── */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography fontSize={13} fontWeight={600} color="text.secondary" mr={1}>Subject:</Typography>
          {[{ name: 'All' }, ...subjects].map(s => {
            const active = activeSubject === s.name;
            const cnt = countFor(s.name);
            return (
              <Badge key={s.name} badgeContent={cnt} color="primary"
                sx={{ '& .MuiBadge-badge': { fontSize: 9, minWidth: 16, height: 16, background: active ? '#fff' : '#1a237e', color: active ? '#1a237e' : '#fff' } }}>
                <Chip
                  label={s.name}
                  clickable
                  onClick={() => handleSubjectTab(s.name)}
                  sx={{
                    fontWeight: 700, fontSize: 12,
                    background: active ? '#1a237e' : '#e8eaf6',
                    color: active ? '#fff' : '#1a237e',
                    '&:hover': { background: active ? '#1a237e' : '#c5cae9' },
                  }}
                />
              </Badge>
            );
          })}
        </CardContent>
      </Card>

      {/* ── Search ── */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            fullWidth size="small" placeholder="Search by title or subject…"
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#9e9e9e' }} /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </CardContent>
      </Card>

      {/* ── Materials Table ── */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : paginated.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <LibraryBooks sx={{ fontSize: 64, color: '#e8eaf6', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={600}>No materials found</Typography>
            <Typography variant="caption" color="text.secondary">
              {search ? 'Try a different search term' : 'Click "Add Material" to get started'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['#', 'Title', 'Subject', 'Type', 'Progress', 'Resources', 'Added', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((m, i) => (
                    <TableRow key={m.ID} hover>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>

                      {/* Title + description */}
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography fontWeight={700} fontSize={13} noWrap>{m.title}</Typography>
                        {m.description && (
                          <Typography fontSize={11} color="text.secondary" noWrap sx={{ maxWidth: 220 }}>
                            {m.description}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Subject */}
                      <TableCell>
                        <Chip label={m.subject} size="small"
                          sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 11 }} />
                      </TableCell>

                      {/* Type */}
                      <TableCell><TypeChip type={m.material_type} /></TableCell>

                      {/* Progress */}
                      <TableCell sx={{ minWidth: 140 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={parseInt(m.progress ?? 0, 10)}
                              sx={{
                                height: 7, borderRadius: 4,
                                backgroundColor: '#e8eaf6',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  background: parseInt(m.progress ?? 0, 10) === 100
                                    ? 'linear-gradient(90deg,#2e7d32,#43a047)'
                                    : 'linear-gradient(90deg,#1a237e,#1565c0)',
                                },
                              }}
                            />
                          </Box>
                          <Typography fontSize={11} fontWeight={700} color="#1a237e" sx={{ minWidth: 30 }}>
                            {m.progress ?? 0}%
                          </Typography>
                          <Tooltip title="Edit Progress">
                            <IconButton size="small" onClick={() => openProgressDialog(m)} sx={{ color: '#1565c0', p: 0.3 }}>
                              <Edit sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* Resource buttons */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {m.youtube_url && (
                            <Tooltip title="Watch Video">
                              <Button size="small" variant="contained" startIcon={<YouTube />}
                                href={m.youtube_url} target="_blank" rel="noopener noreferrer"
                                sx={{ background: '#FF0000', borderRadius: 2, fontWeight: 700, fontSize: 10, '&:hover': { background: '#cc0000' } }}>
                                Watch
                              </Button>
                            </Tooltip>
                          )}
                          {m.file_url && (
                            <Tooltip title="Open PDF / File">
                              <Button size="small" variant="contained" startIcon={<PictureAsPdf />}
                                href={Config.main_url + m.file_url} target="_blank" rel="noopener noreferrer"
                                sx={{ background: '#e53935', borderRadius: 2, fontWeight: 700, fontSize: 10, '&:hover': { background: '#b71c1c' } }}>
                                PDF
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {m.created_at ? dayjs(m.created_at).format('DD MMM YYYY') : '—'}
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => setViewItem(m)} sx={{ color: '#1a237e' }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(m.ID)} sx={{ color: '#c62828' }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={filtered.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }} />
          </>
        )}
      </Card>

      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LibraryBooks fontSize="small" />
          Add Study Material — {selected?.standard} - {selected?.section}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {/* Subject */}
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Subject *" value={form.subject} onChange={f('subject')}>
                <MenuItem value="">— Select Subject —</MenuItem>
                {subjects.map(s => <MenuItem key={s.ID} value={s.name}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>

            {/* Type */}
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Material Type *" value={form.material_type} onChange={f('material_type')}>
                {Object.keys(TYPE_META).map(t => (
                  <MenuItem key={t} value={t}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(TYPE_META[t].icon, { style: { color: TYPE_META[t].color, fontSize: 16 } })}
                      {t}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Title *" value={form.title} onChange={f('title')}
                placeholder="e.g. Chapter 3 – Photosynthesis Notes" />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description / Notes" value={form.description}
                onChange={f('description')} multiline rows={3}
                placeholder="Add any notes, summary or instructions here…" />
            </Grid>

            {/* YouTube URL — shown for Video type */}
            {(form.material_type === 'Video' || form.material_type === 'Link') && (
              <Grid item xs={12}>
                <TextField fullWidth size="small"
                  label={form.material_type === 'Video' ? 'YouTube URL *' : 'Link URL'}
                  value={form.youtube_url} onChange={f('youtube_url')}
                  placeholder="https://youtube.com/watch?v=…"
                  InputProps={{ startAdornment: <InputAdornment position="start"><YouTube sx={{ color: '#FF0000', fontSize: 18 }} /></InputAdornment> }}
                />
              </Grid>
            )}

            {/* PDF Upload — shown for PDF type */}
            {form.material_type === 'PDF' && (
              <Grid item xs={12}>
                <Box sx={{ border: '2px dashed #c5cae9', borderRadius: 2, p: 2.5, textAlign: 'center', cursor: 'pointer', background: '#f8f9ff', '&:hover': { borderColor: '#1a237e', background: '#e8eaf6' } }}
                  onClick={() => document.getElementById('pdf-upload').click()}>
                  <input id="pdf-upload" type="file" accept=".pdf,.doc,.docx,.ppt,.pptx"
                    style={{ display: 'none' }} onChange={e => setPdfFile(e.target.files[0])} />
                  <CloudUpload sx={{ fontSize: 36, color: '#9fa8da', mb: 1 }} />
                  {pdfFile ? (
                    <Typography fontSize={13} fontWeight={700} color="#1a237e">{pdfFile.name}</Typography>
                  ) : (
                    <>
                      <Typography fontSize={13} fontWeight={600} color="#1a237e">Click to upload PDF / Document</Typography>
                      <Typography fontSize={11} color="text.secondary">PDF, DOC, DOCX, PPT, PPTX</Typography>
                    </>
                  )}
                </Box>
              </Grid>
            )}

            {/* YouTube URL optional for Note/PDF too */}
            {(form.material_type === 'Note' || form.material_type === 'PDF') && (
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="YouTube Video URL (optional)"
                  value={form.youtube_url} onChange={f('youtube_url')}
                  placeholder="Attach a reference video (optional)"
                  InputProps={{ startAdornment: <InputAdornment position="start"><YouTube sx={{ color: '#FF0000', fontSize: 18 }} /></InputAdornment> }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => { setAddDialog(false); setPdfFile(null); }} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          VIEW DETAIL DIALOG
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewItem && (
          <>
            <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LibraryBooks fontSize="small" />
                {viewItem.title}
              </Box>
              <TypeChip type={viewItem.material_type} />
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5 }}>
              {/* Meta chips */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip icon={<School sx={{ fontSize: 14 }} />} label={`${viewItem.standard} - ${viewItem.section}`}
                  size="small" sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                <Chip label={viewItem.subject} size="small"
                  sx={{ background: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                {viewItem.created_at && (
                  <Chip label={dayjs(viewItem.created_at).format('DD MMM YYYY')} size="small"
                    sx={{ background: '#f5f5f5', color: '#666', fontWeight: 600 }} />
                )}
              </Box>

              {/* Description */}
              {viewItem.description ? (
                <Box sx={{ p: 2, background: '#f5f6fa', borderRadius: 2, mb: 2.5, borderLeft: '4px solid #1a237e' }}>
                  <Typography fontSize={13} color="#333" lineHeight={1.8} sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewItem.description}
                  </Typography>
                </Box>
              ) : (
                <Typography fontSize={13} color="text.secondary" mb={2}>No description provided.</Typography>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* PDF / File */}
              {viewItem.file_url && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography fontSize={12} fontWeight={700} color="#e53935" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PictureAsPdf fontSize="small" /> PDF / Document
                  </Typography>
                  <Button variant="contained" startIcon={<OpenInNew />}
                    href={Config.main_url + viewItem.file_url} target="_blank" rel="noopener noreferrer"
                    sx={{ background: '#e53935', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#b71c1c' } }}>
                    Open File
                  </Button>
                </Box>
              )}

              {/* YouTube embed */}
              {viewItem.youtube_url && (() => {
                const match = viewItem.youtube_url.match(/(?:youtu\.be\/|v=|embed\/)([^#&?]{11})/);
                const ytId = match?.[1];
                return (
                  <Box>
                    <Typography fontSize={12} fontWeight={700} color="#FF0000" mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <YouTube fontSize="small" /> Video Lesson
                    </Typography>
                    {ytId ? (
                      <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden' }}>
                        <iframe
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                          src={`https://www.youtube.com/embed/${ytId}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen title={viewItem.title}
                        />
                      </Box>
                    ) : (
                      <Button variant="contained" startIcon={<YouTube />}
                        href={viewItem.youtube_url} target="_blank"
                        sx={{ background: '#FF0000', borderRadius: 2, fontWeight: 700, '&:hover': { background: '#cc0000' } }}>
                        Watch on YouTube
                      </Button>
                    )}
                  </Box>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #f0f0f0' }}>
              <Button onClick={() => setViewItem(null)} variant="outlined" sx={{ borderRadius: 2 }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════════════
          PROGRESS EDIT DIALOG
      ══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={progressDialog} onClose={() => setProgressDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit fontSize="small" /> Edit Progress
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {progressItem && (
            <Box>
              <Typography fontSize={13} fontWeight={600} color="#333" mb={2} noWrap>
                {progressItem.title}
              </Typography>

              {/* Progress bar preview */}
              <Box sx={{ mb: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 10, borderRadius: 5,
                    backgroundColor: '#e8eaf6',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 5,
                      background: progressValue === 100
                        ? 'linear-gradient(90deg,#2e7d32,#43a047)'
                        : 'linear-gradient(90deg,#1a237e,#1565c0)',
                    },
                  }}
                />
                <Typography fontSize={13} fontWeight={700} color="#1a237e" textAlign="right" mt={0.5}>
                  {progressValue}%
                </Typography>
              </Box>

              {/* Quick preset buttons */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {[0, 25, 50, 75, 100].map(v => (
                  <Chip
                    key={v}
                    label={`${v}%`}
                    clickable
                    onClick={() => setProgressValue(v)}
                    sx={{
                      fontWeight: 700, fontSize: 12,
                      background: progressValue === v ? '#1a237e' : '#e8eaf6',
                      color: progressValue === v ? '#fff' : '#1a237e',
                    }}
                  />
                ))}
              </Box>

              {/* Manual input */}
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Progress % (0–100)"
                value={progressValue}
                onChange={e => {
                  const v = Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0));
                  setProgressValue(v);
                }}
                inputProps={{ min: 0, max: 100 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setProgressDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSaveProgress} variant="contained" disabled={savingProgress}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 100 }}>
            {savingProgress ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
